import {router} from '../utils/router.util';
import {ANALYTICS_EVENTS, AnalyticsUtil} from '../utils/analytics.util';
import {ToolbarComponent} from '../components/toolbar.component';
import {ButtonComponent} from '../components/button.component';
import {AppFormatter} from '../utils/app.formatter';
import {campaignDetailService} from '../services/campaign-detail.service';
import {calculateMediaPrize, calculateTotalPrize, getCampaignImage} from '../utils/campaign.util';
import {AACampaign, AAMedia} from '@akross/artemis-ads-sdk';
import {ROUTES} from '../constants/routes.constants';
import {checkCarrotAndExecute} from '../utils/carrot.util';
import {isSafariBrowser, openCarrotRedirectInNewTab, openUrlInNewTab} from '../utils/browser.util';
import {logger} from '../utils/logger.util';
import {artemisService} from "../services/artemis.service.ts";

export class ExternalRedirectCampaignDetailPage {
  private container: HTMLElement;
  private campaignId: string | null = null;
  private campaign: AACampaign | null = null;
  private currentPhaseIndex: number = 0;

  constructor(container: HTMLElement) {
    this.container = container;
  }

  async render(campaignId: string): Promise<void> {
    this.campaignId = campaignId;
    logger.debug('[ExternalRedirectCampaignDetailPage] Renderizando página de detalhes, campaignId:', campaignId);

    try {
      // Buscar detalhes da campanha (usa cache primeiro, faz request se não encontrar)
      logger.debug('[ExternalRedirectCampaignDetailPage] Buscando detalhes da campanha...');
      this.campaign = await campaignDetailService.getCampaignDetail(campaignId);

      if (!this.campaign) {
        logger.warnWithContext('ExternalRedirectCampaignDetailPage', 'Campanha não encontrada (nem no cache nem na API), campaignId:', campaignId);
        this.renderError('Campanha não encontrada');
        return;
      }

      // Renderizar diretamente (sem loading, dados já estão no cache)
      console.log('[ExternalRedirectCampaignDetailPage] Campanha carregada com sucesso, renderizando conteúdo:', {
        id: this.campaign.id,
        name: this.campaign.mainData.layout?.title || this.campaign.campaignName || this.campaign.name,
        mediasCount: this.campaign.mainData?.media?.length || 0,
        totalPrize: calculateTotalPrize(this.campaign)
      });

      this.renderContent();
      this.attachEvents();

      AnalyticsUtil.track(ANALYTICS_EVENTS.CAMPAIGN_DETAIL_VIEWED, {
        campaignId: campaignId
      });
    } catch (error: unknown) {
      logger.error('Error loading campaign detail:', error);
      const errorMessage = error instanceof Error ? error.message : 'Tente novamente mais tarde';
      this.renderError('Erro ao carregar campanha', errorMessage);
    }
  }

  private renderError(title: string, message?: string): void {
    this.container.innerHTML = this.buildErrorHTML(title, message);
    this.attachEvents();
  }

  private buildErrorHTML(title: string, message?: string): string {
    return `
      <div class="campaign-detail-page">
        ${ToolbarComponent.render({
          showBackButton: true,
          showMenuButton: false,
          elevation: 2,
          logoImage: '/vivo-ads-logo.svg',
          logoAlt: 'Vivo Ads'
        })}
        <div class="campaigns-state">
          <div class="error-screen">
            <h2>Ops! Algo deu errado</h2>
            <p>${message || title}</p>
            <div id="error-button-container"></div>
          </div>
        </div>
      </div>
    `;
  }

  private renderContent(): void {
    if (!this.campaign) {
      this.renderError('Campanha não encontrada');
      return;
    }

    this.container.innerHTML = this.buildHTML();
  }

  private buildHTML(): string {
    if (!this.campaign) return '';

    const campaignName = this.campaign.mainData?.layout?.title || this.campaign.name || this.campaign.campaignName || '';
    const headerImage = getCampaignImage(this.campaign);

    return `
      <div class="campaign-detail-page">
        ${ToolbarComponent.render({
          title: campaignName,
          showBackButton: true,
          showMenuButton: false,
          elevation: 2,
          backButtonAction: () => router.navigate(ROUTES.HOME),
          logoImage: undefined // Não mostrar logo quando há título
        })}
        <div class="campaign-detail-scroll-container">
          ${this.buildHeaderImage(headerImage, campaignName)}
          ${this.buildContent()}
        </div>
      </div>
    `;
  }

  private buildHeaderImage(headerImage: string, campaignName: string): string {
    if (!headerImage) {
      return '';
    }

    return `
      <div class="campaign-detail-header-image">
        <img src="${this.escapeHtml(headerImage)}" alt="${this.escapeHtml(campaignName)}" class="campaign-header-img" />
      </div>
    `;
  }


  private buildContent(): string {
    if (!this.campaign) return '';

    const medias = this.campaign.mainData?.media || [];

    if (medias.length === 0) {
      return '<div class="error">Nenhuma ação disponível</div>';
    }

    // Só mostrar tabs se houver mais de uma mídia
    const showTabs = medias.length > 1;

    // Renderizar conteúdo de todas as tabs de uma vez
    const phasesContent = medias.map((media, index) => {
      const totalPrize = calculateMediaPrize(media);
      const unit = media.getPostbackBenefits()?.[0]?.unit || 'MB';

      const mediaWithContent = media as any;
      const content = mediaWithContent.content;
      const externalRedirect = content?.externalRedirect;

      // Extrair valores do externalRedirect com fallbacks
      const infoPrimary = externalRedirect?.infoPrimary || media.title || '';
      const infoSecondary = externalRedirect?.infoSecondary || media.description || '';
      const actionButtonLabel = externalRedirect?.actionButtonLabel || 'Acessar';
      const actionLink = externalRedirect?.link || '';

      const isActive = index === this.currentPhaseIndex;

      return `
        <div class="campaign-detail-phase-wrapper ${isActive ? 'active' : ''}" data-phase-index="${index}">
          <div class="campaign-detail-phase-content">
            <div class="campaign-detail-phase-header">
              <h2 class="campaign-detail-phase-title">${this.escapeHtml(infoPrimary)}</h2>
              <div class="campaign-detail-prize">
                <span class="campaign-detail-prize-label">Prêmio:</span>
                <span class="campaign-detail-prize-value">${AppFormatter.formatPrize(totalPrize)} ${unit}</span>
              </div>
            </div>
            ${infoSecondary 
              ? `<p class="campaign-detail-phase-description">${this.formatTextWithLineBreaks(infoSecondary)}</p>`
              : ''
            }
          </div>
          ${this.buildActionButton({
            actionButtonText: actionButtonLabel || undefined,
            actionButtonUrl: actionLink || undefined
          }, index)}
        </div>
      `;
    }).join('');

    return `
      <div class="campaign-detail-content">
        ${showTabs ? this.buildTabs(medias.length) : ''}
        <div class="campaign-detail-phases-container">
          ${phasesContent}
        </div>
      </div>
    `;
  }

  private getCurrentMedia(): AAMedia | null {
    if (!this.campaign) return null;

    const medias = this.campaign.mainData?.media || [];
    if (medias.length === 0) {
      return null;
    }

    return medias[this.currentPhaseIndex] || medias[0] || null;
  }

  private buildTabs(mediasCount: number): string {
    return `
      <div class="campaign-detail-tabs">
        ${Array.from({ length: mediasCount }, (_, index) => `
          <button 
            class="campaign-detail-tab ${index === this.currentPhaseIndex ? 'active' : ''}"
            data-phase-index="${index}"
          >
            Fase ${index + 1}
          </button>
        `).join('')}
      </div>
    `;
  }


  private buildActionButton(action?: { actionButtonText?: string; actionButtonUrl?: string;}, phaseIndex?: number): string {
    // Se não houver texto do botão, não exibir
    if (!action?.actionButtonText) {
      return '';
    }

    const buttonId = phaseIndex !== undefined ? `detail-action-btn-${phaseIndex}` : 'detail-action-btn';
    const actionUrl = action?.actionButtonUrl || '#';
    const hasUrl = !!action?.actionButtonUrl && actionUrl !== '#';

    // Retornar um container onde o botão será inserido via JavaScript
    return `
      <div 
        class="campaign-detail-action-button-container"
        id="${buttonId}-container"
        data-action-url="${this.escapeHtml(actionUrl)}"
        data-has-url="${hasUrl}"
        data-button-text="${this.escapeHtml(action.actionButtonText)}"
      ></div>
    `;
  }

  private attachEvents(): void {
    // Função para voltar - sempre voltar para home
    const goBack = () => {
      router.navigate(ROUTES.HOME);
    };

    // Toolbar events
    ToolbarComponent.attachEvents(this.container, {
      showBackButton: true,
      backButtonAction: goBack,
      showMenuButton: false
    });

    // Botão de voltar na tela de erro (usando ButtonComponent para manter consistência)
    const errorButtonContainer = document.getElementById('error-button-container');
    if (errorButtonContainer) {
      const backButton = ButtonComponent.createPrimaryButton({
        text: 'Voltar',
        id: 'error-back-button',
        fullWidth: true,
        onClick: goBack
      });
      errorButtonContainer.appendChild(backButton);
    }

    // Tabs (só se houver mais de uma fase)
    const tabs = this.container.querySelectorAll('.campaign-detail-tab');
    if (tabs.length > 0) {
      tabs.forEach((tab) => {
        tab.addEventListener('click', () => {
          const phaseIndex = parseInt(tab.getAttribute('data-phase-index') || '0', 10);
          this.switchPhase(phaseIndex);
        });
      });
    }

    // Botões de ação (um para cada fase) - criar usando ButtonComponent
    const actionButtonContainers = this.container.querySelectorAll('.campaign-detail-action-button-container');
    actionButtonContainers.forEach((container) => {
      const actionUrl = container.getAttribute('data-action-url');
      const hasUrl = container.getAttribute('data-has-url') === 'true';
      const buttonText = container.getAttribute('data-button-text') || 'Acessar';
      const buttonId = container.id.replace('-container', '');

      // Criar botão usando ButtonComponent
      const actionButton = ButtonComponent.createPrimaryButton({
        text: buttonText,
        id: buttonId,
        fullWidth: true,
        disabled: !hasUrl,
        onClick: () => {
          this.onActionClick(actionUrl);
        }
      });

      // Inserir o botão no container
      container.appendChild(actionButton);
    });
  }

  private switchPhase(index: number): void {
    if (!this.campaign) return;

    this.currentPhaseIndex = index;

    // Atualizar estado ativo das tabs
    const tabs = this.container.querySelectorAll('.campaign-detail-tab');
    tabs.forEach((tab, tabIndex) => {
      if (tabIndex === index) {
        tab.classList.add('active');
      } else {
        tab.classList.remove('active');
      }
    });

    // Alternar visibilidade dos conteúdos das fases
    const phaseWrappers = this.container.querySelectorAll('.campaign-detail-phase-wrapper');
    phaseWrappers.forEach((wrapper, wrapperIndex) => {
      if (wrapperIndex === index) {
        wrapper.classList.add('active');
      } else {
        wrapper.classList.remove('active');
      }
    });

    AnalyticsUtil.track(ANALYTICS_EVENTS.CAMPAIGN_PHASE_SWITCHED, {
      campaignId: this.campaignId,
      phaseIndex: index
    });
  }

  private async onActionClick(actionUrl?: string | null): Promise<void> {
    if (!this.campaign) return;

    const currentMedia = this.getCurrentMedia();
    const url = actionUrl || currentMedia?.content?.link || currentMedia?.attributionUrl || this.campaign.attributionUrl;

    // Disparar evento de analytics quando o usuário clica (antes da verificação do pacote cenoura)
    AnalyticsUtil.track(ANALYTICS_EVENTS.CAMPAIGN_ACTION_CLICKED, {
      campaignId: this.campaignId,
      action: 'challenge',
      phaseIndex: this.currentPhaseIndex
    });

    artemisService.sendEvent('media-click', currentMedia?.campaignId!, currentMedia?.requestId!, currentMedia?.uuid);

    // Safari: abrir página de redirecionamento em nova aba no mesmo gesto. Outros: dialog atual.
    if (url && isSafariBrowser()) {
      openCarrotRedirectInNewTab(url);
      artemisService.sendEvent('clickthrough', currentMedia?.campaignId!, currentMedia?.requestId!, currentMedia?.uuid);
      return;
    }
    await checkCarrotAndExecute(() => {
      if (url) {
        openUrlInNewTab(url);
        artemisService.sendEvent('clickthrough', currentMedia?.campaignId!, currentMedia?.requestId!, currentMedia?.uuid);
      }
    });
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Formata texto respeitando quebras de linha
   * Converte \n em <br> e preserva outras formatações
   */
  private formatTextWithLineBreaks(text: string): string {
    if (!text) return '';

    // Escapar HTML primeiro
    const escaped = this.escapeHtml(text);

    // Converter quebras de linha em <br>
    return escaped.replace(/\n/g, '<br>');
  }
}

